/**
 * Response Pair Header Component
 * Renders the header section of a response pair (mode chips + user message)
 */

import React from 'react';
import { ChatMessage } from '@/types/chatTypes';
import { ModeChip } from '@/constants/chatScreenData';
import { Chip } from '@/design-system';
import {
  spacing,
  typographyColors,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import { AudioPlayer } from '../../AudioPlayer';
import { ExpandableText } from '@/components/common/ExpandableText';

export interface ResponsePairHeaderProps {
  userMessage: ChatMessage;
  modeChips: ModeChip[];
  activeMode?: string;
  onModeChange?: (modeId: string) => void;
  intent?: string;
  mood?: string;
  getIcon: (iconName?: string) => React.ReactNode;
  headerRef?: React.RefObject<HTMLDivElement>;
}

export const ResponsePairHeader: React.FC<ResponsePairHeaderProps> = ({
  userMessage,
  modeChips,
  activeMode,
  onModeChange,
  intent,
  mood,
  getIcon,
  headerRef,
}) => {
  return (
    <>
      {/* Mode Chips */}
      <div
        ref={headerRef}
        style={{
          display: 'flex',
          gap: spacing[2],
          flexWrap: 'wrap',
        }}
        className='flex-wrap scroll-mt-[80px] md:scroll-mt-0 mt-4 md:mt-0'
      >
        {modeChips.map(chip => (
          <Chip
            key={chip.id}
            label={chip.label}
            icon={getIcon(chip.icon)}
            isActive={activeMode === chip.id}
            onClick={() => onModeChange?.(chip.id)}
          />
        ))}
        {/* Intent and Mood Chips */}
        {intent && (
          <Chip label={intent} icon={getIcon('sun')} isActive={true} />
        )}
        {mood && (
          <Chip label={mood} icon={getIcon('directions')} isActive={true} />
        )}
      </div>

      {/* User Prompt Text */}
      <div
        style={{
          display: 'flex',
          gap: spacing[2],
          alignItems: 'center',
          justifyContent: 'center',
          paddingLeft: spacing[0],
          paddingRight: spacing[0],
          paddingTop: spacing[2],
          paddingBottom: spacing[2],
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            flex: '1 0 0',
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[3],
          }}
        >
          <ExpandableText
            text={userMessage.content}
            maxLines={5}
            showMoreText='Show more'
            showLessText='Show less'
            style={{
              fontFamily: fontPrimary,
              fontWeight: typography.weight.semibold,
              fontSize: typography.size['2xl'],
              lineHeight: 'normal',
              color: typographyColors.primary,
              margin: 0,
              whiteSpace: 'pre-wrap',
            }}
          />

          {/* Transcript for audio messages */}
          {userMessage.transcription && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing[1],
              }}
            >
              <span
                style={{
                  fontFamily: fontPrimary,
                  fontWeight: typography.weight.medium,
                  fontSize: typography.size.sm,
                  color: typographyColors.secondary,
                }}
              >
                Transcript
              </span>
              <ExpandableText
                text={userMessage.transcription}
                maxLines={5}
                showMoreText='Show more'
                showLessText='Show less'
                style={{
                  fontFamily: fontPrimary,
                  fontSize: typography.size.base,
                  lineHeight: 'normal',
                  color: typographyColors.primary,
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                }}
              />
            </div>
          )}

          {/* Audio Player for audio messages */}
          {userMessage.audioBase64 && (
            <AudioPlayer audioBase64={userMessage.audioBase64} />
          )}
        </div>
      </div>
    </>
  );
};
